import { Button } from 'antd';
import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';
import { OSCAR_MARK_SIZE } from '../../constants/AIAgentConstants';
import { useOscarTheme } from '../../context/OscarRoleContext';
import OscarMark from '../shared/OscarMark';

interface OscarEdgeTabProps {
	onClick: () => void;
	className?: string;
}

/**
 * Entry point that opens the Oscar drawer: a tab tucked into the bottom-right
 * edge of the screen on every page, per the "AI Summarization: WO details"
 * prototype. It replaces the launcher pill that sat at the end of the top bar.
 *
 * At rest the owl shows in full, with the same space to its right as to its
 * left; the rest of the tab is off-screen. Hovered (or focused), the tab slides
 * out further. On touch screens there is no hover, so the resting part is the
 * tap target.
 *
 * Resting offset: the tab is 80px, and the visible part is the 2px stroke plus
 * 8px padding, the 32px owl, then the same 10px again — 52px, so 28px is
 * off-screen.
 *
 * An antd `Button` for focus and keyboard handling, stripped of its own chrome
 * — the tab's look lives on the inner span. `!` because antd's stylesheet loads
 * after Tailwind. Transforms are arbitrary values: preflight is off, so the
 * `translate-*` utilities compile to undefined variables and do nothing.
 */
export const OscarEdgeTab = ({ onClick, className }: OscarEdgeTabProps) => {
	const theme = useOscarTheme();
	return (
		<Button
			type="text"
			aria-label="Open Oscar"
			onClick={onClick}
			className={cn(
				'!fixed bottom-14 right-0 z-[998] !h-auto !border-none !bg-transparent !p-0 !shadow-none',
				'[transform:translateX(28px)] hover:[transform:translateX(0)] focus-visible:[transform:translateX(0)]',
				'!duration-200 motion-reduce:!transition-none',
				className
			)}
		>
			<span
				className={cn(
					// `!flex`: antd's `.ant-btn > span { display: inline-block }` outranks
					// `flex`, which dropped `items-center` and sat the owl on the baseline.
					'!flex h-12 w-20 items-center rounded-l-full pl-xs',
					theme.accentSoft,
					theme.stroke,
					'border-r-0'
				)}
			>
				<OscarMark size={OSCAR_MARK_SIZE.launcher} />
			</span>
		</Button>
	);
};

export default OscarEdgeTab;
